import { useEffect, useRef, useState } from "react";
import { RichEditor } from "@/components/RichEditor";
import { GalleryEditor } from "@/components/Gallery";
import { CollectionPicker } from "@/components/CollectionBits";
import { BlocksEditor } from "@/components/BlocksEditor";
import { charOnlyFilled, presetBlocks, quoteEmpty, restoreBlocks, textPairs } from "@/lib/blocks";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Link2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useWorld } from "@/context/WorldContext";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { ImageUpload } from "@/components/ImageUpload";
import { InfoboxEditor } from "@/components/InfoboxEditor";
import { cleanSections, isFilled, prepareSections } from "@/lib/infobox";

const blank = (sp) => ({
  title: sp.get("title") || "", category: sp.get("category") || "Characters", summary: "", content: "",
  image: null, tags: [], event_date: "", event_order: null, sections: [], gallery: [], collection_ids: [], blocks: (sp.get("category") || "Characters") === "Characters" ? presetBlocks() : [], pinned_quote: null,
});

const LinkPicker = ({ onPick }) => {
  const { pages } = useWorld();
  const [q, setQ] = useState("");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300" data-testid="insert-link-btn"><Link2 className="w-3.5 h-3.5" /> Insert link</button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 bg-panel border-line">
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a page" className="h-8 mb-2" data-testid="link-picker-search" />
        <div className="max-h-56 overflow-y-auto">
          {pages.filter((p) => p.title.toLowerCase().includes(q.toLowerCase())).map((p) => (
            <button key={p.id} type="button" onClick={() => onPick(p.title)} className="block w-full text-left text-sm px-2 py-1.5 rounded text-slate-300 hover:bg-amber-500/10 hover:text-amber-200" data-testid={`link-option-${p.id}`}>{p.title}</button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function PageEditor() {
  const { pageId } = useParams();
  const [sp] = useSearchParams();
  const { world, pages, basePath, reload, collections, scope: profileScope } = useWorld();
  const nav = useNavigate();
  const existing = pages.find((p) => p.id === pageId);
  const [f, setF] = useState(() => {
    const base = existing ? { ...existing } : blank(sp);
    return { ...base, sections: prepareSections(base.sections || [], base.category) };
  });
  const [tags, setTags] = useState((existing?.tags || []).join(", "));
  const [busy, setBusy] = useState(false);
  const suggest = sp.get("suggest") === "1";
  const [message, setMessage] = useState("");
  const [pendingNote, setPendingNote] = useState(false);
  useEffect(() => {
    if (!existing) return;
    api.get(`/pages/${existing.id}`).then(({ data }) => {
      const p = data.my_pending;
      if (p?.data && (suggest ? p.kind === "suggestion" : p.kind === "change")) {
        setF({ ...p.data, sections: prepareSections(p.data.sections || [], p.data.category) });
        setTags((p.data.tags || []).join(", "));
        setPendingNote(true);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const ed = useRef(null);
  const [cols, setCols] = useState(existing?.collection_ids || []);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const stash = useRef(null);
  // Leaving Characters keeps written text as custom sections; coming back restores the character layout
  const applyCategory = (v) => {
    let blocks = f.blocks || [];
    let pinned = f.pinned_quote;
    if (f.category === "Characters" && v !== "Characters") {
      const pairs = textPairs(blocks, pinned);
      stash.current = { blocks, pinned, pairs };
      blocks = pairs.map((p) => p.blk);
    } else if (v === "Characters" && f.category !== "Characters") {
      blocks = stash.current ? restoreBlocks(stash.current.pairs, stash.current.blocks, blocks) : [...presetBlocks(), ...blocks];
      pinned = stash.current ? stash.current.pinned : pinned;
    }
    setF({ ...f, category: v, sections: prepareSections(cleanSections(f.sections), v), blocks, pinned_quote: pinned });
  };
  const [askSwitch, setAskSwitch] = useState(null);
  const setCategory = (v) => {
    const lost = f.category === "Characters" && v !== "Characters" ? charOnlyFilled(f.blocks || [], f.pinned_quote) : [];
    return lost.length ? setAskSwitch({ to: v, lost }) : applyCategory(v);
  };
  const isChar = f.category === "Characters";
  const scope = world.my_role === "editor" && !suggest && !world.my_scope?.all ? world.my_scope?.categories || [] : null;
  const allowedCat = (k) => !scope || scope.includes(k) || k === existing?.category;
  useEffect(() => { if (!existing && scope?.length && !scope.includes(f.category)) setCategory(scope[0]); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const insertLink = (title) => ed.current?.chain().focus().insertContent(`[[${title}]] `).run();

  const save = async (e) => {
    e.preventDefault();
    if (f.category === "Ships") {
      const info = f.sections.find((s) => s.title === "Ship Information")?.facts || [];
      const members = (info.find((x) => x.key === "Characters")?.items || []).filter(isFilled);
      if (new Set(members.map((m) => m.page_id || m.text.trim().toLowerCase())).size < 2) return toast.error("A ship needs at least two characters. Add them under Ship Information → Characters.");
    }
    setBusy(true);
    const body = {
      title: f.title, category: f.category, summary: f.summary, content: f.content, image: f.image,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean), event_date: f.event_date,
      event_order: f.event_order === "" || f.event_order == null ? null : Number(f.event_order),
      sections: cleanSections(f.sections), gallery: f.gallery || [], blocks: f.blocks || [],
      pinned_quote: isChar && !quoteEmpty(f.pinned_quote) ? f.pinned_quote : null,
    };
    try {
      if (suggest) {
        await api.post(`/pages/${existing.id}/suggest`, { ...body, message });
        toast.success("Suggestion sent for review");
        return nav(`${basePath}/p/${existing.id}`);
      }
      const put = (force) => api.put(`/pages/${existing.id}`, body, { params: { base: existing.updated_at, force } });
      let res;
      try {
        res = existing ? await put(false) : await api.post(`/worlds/${world.id}/pages`, body, { params: profileScope });
      } catch (err) {
        if (err.response?.status !== 409 || !window.confirm(`${formatApiError(err)}\n\nSave anyway and replace the live version? Choose Cancel to open the live page in a new tab and compare.`)) {
          if (err.response?.status === 409) window.open(`${basePath}/p/${existing.id}`, "_blank");
          throw err;
        }
        res = await put(true);
      }
      const { data } = res;
      if (data.pending) {
        toast.success("New entry sent for review");
        await reload();
        return nav(`${basePath}/pending?c=${data.change_id}`);
      }
      if (data.my_pending && world.review_required && world.my_role === "editor") {
        toast.success("Saved. Your changes are waiting for review");
        await reload();
        return nav(`${basePath}/p/${data.id}`);
      }
      const valid = collections.filter((c) => c.category === f.category).map((c) => c.id);
      const want = cols.filter((id) => valid.includes(id));
      if (JSON.stringify(want) !== JSON.stringify(data.collection_ids || [])) await api.put(`/pages/${data.id}/collections`, { collection_ids: want });
      await reload();
      toast.success("Page saved to the codex");
      nav(`${basePath}/p/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="px-6 md:px-10 py-10 grid xl:grid-cols-[minmax(0,1fr)_380px] gap-10 max-w-[1700px]" data-testid="page-editor">
      <div className="space-y-5 min-w-0">
        <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80">{suggest ? "Suggesting changes" : existing ? "Revising" : "New entry"}</p>
        {suggest && <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200" data-testid="suggest-notice">You can't edit this entry directly. Your version goes to the owner or an admin for review.</p>}
        {pendingNote && <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200" data-testid="pending-edit-notice">You're editing your version that's waiting for review. The live page hasn't changed yet.</p>}
        <Input required value={f.title} onChange={set("title")} placeholder="Page title"
          className="h-auto py-2 font-display !text-4xl bg-transparent border-0 border-b rounded-none px-0 focus-visible:ring-0 text-amber-50" data-testid="page-title-input" />
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Category</Label>
            <Select value={f.category} onValueChange={setCategory}>
              <SelectTrigger data-testid="page-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-panel">
                {CATEGORIES.filter((c) => allowedCat(c.key)).map((c) => <SelectItem key={c.key} value={c.key} data-testid={`category-option-${c.key.toLowerCase()}`}>{c.key}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Tags (comma separated)</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="noble, exiled" data-testid="page-tags-input" /></div>
        </div>
        {f.category === "Events" && (
          <div className="grid sm:grid-cols-2 gap-4 rounded-xl border border-rose-500/30 bg-rose-500/5 p-4" data-testid="event-fields">
            <div className="space-y-1.5"><Label>In-world date</Label>
              <Input value={f.event_date} onChange={set("event_date")} placeholder="Year 312 of the Low Tide" data-testid="event-date-input" /></div>
            <div className="space-y-1.5"><Label>Timeline order (number)</Label>
              <Input type="number" step="any" value={f.event_order ?? ""} onChange={set("event_order")} placeholder="312" data-testid="event-order-input" /></div>
          </div>
        )}
        <div className="space-y-1.5"><Label>Summary</Label>
          <Input value={f.summary} onChange={set("summary")} placeholder="One line that captures this page" data-testid="page-summary-input" /></div>
        <div className="space-y-1.5">
          <Label>{isChar ? "Sections" : "Content"} <span className="text-slate-500 font-normal">· type [[ to link another page</span></Label>
          {!isChar && <RichEditor value={f.content} onChange={(content) => setF((s) => ({ ...s, content }))} pages={pages.filter((p) => p.id !== existing?.id)}
            onReady={(e) => { ed.current = e; }} toolbarExtra={<LinkPicker onPick={insertLink} />} />}
          <BlocksEditor blocks={f.blocks || []} setBlocks={(blocks) => setF((s) => ({ ...s, blocks }))} character={isChar}
            pinned={f.pinned_quote} setPinned={(pinned_quote) => setF((s) => ({ ...s, pinned_quote }))} pages={pages.filter((p) => p.id !== existing?.id)}
            sections={f.sections} setSections={(sections) => setF((s) => ({ ...s, sections }))} />
        </div>
        {suggest && <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Optional message for the reviewer, e.g. Fixed a typo" data-testid="suggest-message-input" />}
        <div className="flex gap-3">
          <Button type="submit" disabled={busy} className="rounded-full px-8 bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="save-page-btn">{busy ? "Saving..." : suggest ? "Send suggestion" : world.review_required && world.my_role === "editor" ? "Submit for review" : "Save page"}</Button>
          <Button type="button" variant="ghost" onClick={() => nav(-1)} data-testid="cancel-edit-btn">Cancel</Button>
        </div>
      </div>
      <aside className="space-y-5">
        <div className="space-y-1.5"><Label>Portrait / map</Label>
          <ImageUpload value={f.image} onChange={set("image")} worldId={world.id} testId="page-image" tall /></div>
        <div className="space-y-1.5"><Label>Gallery <span className="text-slate-500 font-normal">· extra images with captions</span></Label>
          <GalleryEditor items={f.gallery || []} onChange={(fn) => setF((s) => ({ ...s, gallery: fn(s.gallery || []) }))} worldId={world.id} /></div>
        <div className="space-y-1.5"><Label>Collections</Label>
          <CollectionPicker category={f.category} value={cols} onChange={setCols} /></div>
        <div className="space-y-2">
          <Label>Infobox</Label>
          <InfoboxEditor sections={f.sections} onChange={(sections) => setF((s) => ({ ...s, sections }))} category={f.category} pages={pages} selfId={existing?.id} title={f.title} />
        </div>
      </aside>
      <AlertDialog open={!!askSwitch} onOpenChange={(o) => !o && setAskSwitch(null)}>
        <AlertDialogContent className="bg-panel border-line" data-testid="category-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-amber-50">Switch to {askSwitch?.to}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              These sections only exist on character pages: <span className="text-amber-200" data-testid="category-switch-lost">{askSwitch?.lost.join(", ")}</span>.
              Your writing in them is kept as plain text sections. Switch back to Characters before saving to get them back as they were.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="category-switch-cancel">Stay a character</AlertDialogCancel>
            <AlertDialogAction onClick={() => { applyCategory(askSwitch.to); setAskSwitch(null); }} className="bg-amber-500 hover:bg-amber-400 text-onaccent" data-testid="category-switch-confirm">Switch and keep as text</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
